import { useEffect, useRef } from 'react';

import {
  notifyComscoreEnterForeground,
  notifyComscoreExitForeground,
} from '@/services/comscore';

export const useDeviceWake = (onWake: () => void, onSleep?: () => void) => {
  const isPageHidden = useRef(false);
  const wakeTimeout = useRef<NodeJS.Timeout>();

  useEffect(() => {
    const handlePageHide = () => {
      onSleep?.();
      if (!isPageHidden.current) {
        notifyComscoreExitForeground();
      }
      isPageHidden.current = true;
      if (wakeTimeout.current) {
        clearTimeout(wakeTimeout.current);
      }
    };

    const handlePageShow = () => {
      if (isPageHidden.current) {
        notifyComscoreEnterForeground();
        wakeTimeout.current = setTimeout(() => {
          onWake();
          isPageHidden.current = false;
        }, 500);
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        handlePageHide();
      } else {
        handlePageShow();
      }
    };

    if (supportsVisibilityAPI()) {
      document.addEventListener('visibilitychange', handleVisibilityChange);
    } else {
      window.addEventListener('focus', handlePageShow);
      window.addEventListener('blur', handlePageHide);
    }

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handlePageShow);
      window.removeEventListener('blur', handlePageHide);
      if (wakeTimeout.current) {
        clearTimeout(wakeTimeout.current);
      }
    };
  }, [onSleep, onWake]);
};

const supportsVisibilityAPI = () => 'hidden' in document;
